/**
 * main.tsx — App bootstrap entry point.
 *
 * BOOT ARCHITECTURE RULES (do not violate):
 * 1. This file must stay minimal — only React, ReactDOM, and index.css are
 *    statically imported. Everything else is dynamically imported.
 * 2. A visible branded shell (AppStartupScreen) is rendered IMMEDIATELY,
 *    before App.tsx is imported. First paint must never depend on awaited
 *    restore logic, auth, data fetching, or any provider initialisation.
 * 3. If the App.tsx dynamic import fails (stale chunks, network), a recovery
 *    UI with Retry / Hard Refresh is shown — never a blank screen.
 * 4. One automatic reload per session is attempted for chunk-load failures
 *    (guarded by sessionStorage flag to prevent infinite loops).
 */
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const root = createRoot(rootElement);

// ── Helpers ──────────────────────────────────────────────────────────

function log(stage: string) {
  const ts = new Date().toISOString();
  console.info(`[boot] ${stage} @ ${ts}`);
  try {
    sessionStorage.setItem("__dino_boot_stage__", `${stage} @ ${ts}`);
  } catch { /* quota */ }
}

/** Unregister service-workers + clear CacheStorage. Best-effort, never throws. */
async function cleanupCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (e) {
    console.warn("[boot] cache cleanup failed", e);
  }
}

/** Race a promise against a timeout. Resolves the promise value or rejects. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timeout: ${label} (${ms}ms)`)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

// ── Boot ─────────────────────────────────────────────────────────────

const RELOAD_KEY = "__dino_boot_reload_done__";

async function mountApp(): Promise<void> {
  // 1. Immediate visual feedback — no waiting on anything
  log("rendering_shell");
  const { default: StartupScreen } = await import("./components/AppStartupScreen.tsx");
  root.render(React.createElement(StartupScreen, { stage: "Starting…" }));

  // 2. Background cache cleanup (fire-and-forget with timeout — never blocks mount)
  cleanupCaches().catch(() => {});

  // 3. Import the real app with a hard timeout
  try {
    log("importing_app");
    const { default: App } = await withTimeout(
      import("./App.tsx"),
      30_000,
      "App import",
    );

    log("mounting_app");
    root.render(React.createElement(App));
    log("mounted");

    // Clear any reload guard on success
    try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* */ }
  } catch (error) {
    console.error("[boot] App mount failed", error);

    const message = error instanceof Error ? error.message : String(error);
    const isDynamicImportFailure =
      /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Timeout:/i.test(message);

    // One automatic reload attempt per session for dynamic-import failures
    if (isDynamicImportFailure && !sessionStorage.getItem(RELOAD_KEY)) {
      log("auto_reload");
      sessionStorage.setItem(RELOAD_KEY, "1");
      await cleanupCaches();
      window.location.reload();
      return;
    }

    // Show recovery UI — user can retry or hard-refresh
    log("showing_recovery");
    root.render(
      React.createElement(StartupScreen, {
        error,
        onRetry: () => {
          sessionStorage.removeItem(RELOAD_KEY);
          void mountApp();
        },
        onHardRefresh: () => {
          sessionStorage.clear();
          void cleanupCaches().then(() => window.location.reload());
        },
      }),
    );
  }
}

void mountApp();
